import React from "react";
import { useState, useEffect } from "react";
import { useDebouncedCallback } from 'use-debounce';
import { Input } from "@/components/ui/input";
import { Search, X } from "lucide-react";
import { Button } from "@/components/ui/button";

interface SearchBarProps {
  onSearch: (query: string) => void;
  placeholder?: string;
  className?: string;
  value?: string;
}

const SearchBar = ({ onSearch, placeholder = "Search products...", className = "", value = "" }: SearchBarProps) => {
  const [query, setQuery] = useState(value);
  const [isFocused, setIsFocused] = useState(false);
  const [isVisible, setIsVisible] = useState(false);

  // Update internal query state when value prop changes
  useEffect(() => {
    setQuery(value);
  }, [value]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    onSearch(query);
  };

  // Debounced search handler (300ms like admin portal)
  const debouncedSearch = useDebouncedCallback((query: string) => {
    onSearch(query);
  }, 300);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newQuery = e.target.value;
    setQuery(newQuery);
    // Use debounced search for smooth typing experience
    debouncedSearch(newQuery);
  };

  const handleClear = () => {
    setQuery("");
    // Clear search immediately (not debounced)
    onSearch("");
  };

  // Effect to animate the search bar when it mounts
  useEffect(() => {
    // Set visibility after component mounts
    setIsVisible(true);
  }, []);

  return (
    <form 
      onSubmit={handleSearch} 
      className={`relative ${className} transform transition-all duration-300 ease-in-out ${
        isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-5"
      }`}
    >
      <div 
        className={`relative flex items-center rounded-lg transition-all duration-300 ${
          isFocused 
            ? "shadow-md shadow-jamaica-green/10 ring-1 ring-jamaica-green/20 bg-white" 
            : "bg-gray-50 hover:bg-gray-100/80"
        }`}
      >
        <Search 
          className={`absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 transition-colors duration-200 ${
            isFocused ? "text-jamaica-green" : "text-gray-400"
          }`} 
        />
        <Input
          type="text"
          placeholder={placeholder}
          value={query}
          onChange={handleInputChange}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          className="pl-12 pr-12 py-6 h-14 w-full bg-transparent border-none focus-visible:ring-0 text-base focus:placeholder:text-jamaica-green/50"
        />
        {query && (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="absolute right-2 top-1/2 -translate-y-1/2 h-10 w-10 hover:bg-gray-100 transition-all duration-200"
            onClick={handleClear}
          >
            <X className="h-5 w-5 text-gray-400 hover:text-jamaica-green" />
          </Button>
        )}
      </div>
    </form>
  );
};

export default SearchBar;
